import { Component, Input, Inject, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { differenceInCalendarDays } from 'date-fns';
import { ScheduledPaymentPeriodType } from '@nano/scheduled-payments/common';

@UntilDestroy()
@Component({
  selector: 'app-scheduled-payment',
  templateUrl: './scheduled-payment.component.html',
  standalone: false
})
export class ScheduledPaymentComponent implements OnInit, OnDestroy {
  @Input() scheduledPaymentsEnabled: boolean;
  @Input() editStandingOrderId: boolean;
  @Input() scheduledControl: AbstractControl;
  @Input() scheduledStartDateControl: AbstractControl;
  @Input() scheduledEndDateControl: AbstractControl;
  @Input() scheduledFrequencyControl: AbstractControl;

  private readonly startDate = new Date();

  readonly ScheduledPaymentPeriodType = ScheduledPaymentPeriodType;
  readonly scheduledPaymentPeriodTypes: string[] = Object.keys(ScheduledPaymentPeriodType);

  startDateDisabled = (current: Date): boolean => differenceInCalendarDays(current, this.startDate) < 0;
  endDateDisabled = (current: Date): boolean => differenceInCalendarDays(current, this.startDate) < 0;

  constructor(@Inject(L10N_LOCALE) public locale: L10nLocale) {}

  ngOnInit(): void {
    this.scheduledFrequencyControl.valueChanges.pipe(untilDestroyed(this)).subscribe((scheduledFrequency: ScheduledPaymentPeriodType) => {
      this.handleScheduledFrequencyChange(scheduledFrequency);
    });

    this.scheduledControl.valueChanges.pipe(untilDestroyed(this)).subscribe((scheduled: boolean) => {
      this.handleScheduledChange(scheduled);
    });

    this.handleScheduledChange(this.scheduled);
  }

  private handleScheduledFrequencyChange(scheduledFrequency: ScheduledPaymentPeriodType): void {
    if (scheduledFrequency === ScheduledPaymentPeriodType.DO_NOT_REPEAT) {
      this.scheduledEndDateControl.setValue(null);
      this.scheduledEndDateControl.disable();
    } else {
      this.scheduledEndDateControl.enable();
    }
  }

  private handleScheduledChange(scheduled: boolean): void {
    if (scheduled) {
      this.scheduledStartDateControl.enable();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.scheduledStartDateControl.setValue(tomorrow);
    } else {
      this.scheduledStartDateControl.disable();
      this.scheduledFrequencyControl.setValue(ScheduledPaymentPeriodType.DO_NOT_REPEAT);
    }
  }

  ngOnDestroy(): void {
    this.scheduledFrequencyControl.setValue(null);
  }

  get scheduled(): boolean {
    return this.scheduledControl?.value as boolean;
  }

  get scheduledStartDate(): Date {
    return this.scheduledStartDateControl?.value as Date;
  }

  get scheduledEndDate(): Date {
    return this.scheduledEndDateControl?.value as Date;
  }

  get scheduledFrequency(): ScheduledPaymentPeriodType {
    return this.scheduledFrequencyControl?.value as ScheduledPaymentPeriodType;
  }
}
